<template>
  <div
    id="element"
    class="reset-password"
  >
    <div class="reset-password--item">
      <Header/>
    </div>
    <vue-recaptcha
      ref="recaptcha"
      :sitekey="siteKey"
      size="invisible"
      @verify="reset"
      @expired="onCaptchaExpired"
    />
    <div class="section-gray">
      <div class="section-white">
        <div class="reset-password--title">
          <div style="margin-bottom: 32px;">
            <span class="status-name">
              {{ $t('reset_password.reset_password') }}
            </span>
          </div>
        </div>
        <div class="is-expanded reset-password-field-box">
          <b-field
            :label="`${$t('register.password')}:`"
            class="reset-password-password-field"
          >
            <span class="reset-password-field-note-text">
              {{ $t('register.password_condition') }}
            </span>
            <b-input
              v-validate="{ required: true, max: 120, regex: /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9]).{8,}$/ }"
              v-model="newPassword"
              :disabled="isLoading"
              :placeholder="$t('register.password_placeholder')"
              class="password-input"
              name="password"
              type="password"
              password-reveal
            />
          </b-field>
          <PasswordMeter
            v-model="newPassword"
            :secure-length="8"
            strength-meter-only
            class="password-meter"
            @score="onPasswordScoreChange"
          />
          <span
            v-if="passwordMeterLabel"
            :class="['password-meter--score', {
              'very-weak': passwordScore === 0,
              'weak': passwordScore === 1,
              'normal': passwordScore === 2,
              'strong': passwordScore === 3,
              'very-strong': passwordScore === 4
            }]"
          >
            {{ $t(`register.password_meter_${passwordScore}`) }}
          </span>
          <div class="is-invalid">{{ errors.first('password') }}</div>
        </div>
        <div class="is-expanded reset-password-field-box">
          <b-field :label="`${$t('register.confirm_password')}:`">
            <b-input
              v-validate="{ required: true, is: newPassword, max: 120 }"
              v-model="confirmNewPassword"
              :disabled="isLoading"
              :placeholder="$t('register.confirm_password_placeholder')"
              name="confirm password"
              type="password"
              password-reveal
            />
          </b-field>
          <div class="is-invalid">{{ errors.first('confirm password') }}</div>
        </div>
        <b-button
          :loading="isLoading"
          class="submit-btn"
          @click="executeRecaptcha"
        >
          {{ $t('reset_password.reset_password') }}
        </b-button>
      </div>
    </div>
    <div class="reset-password--item">
      <Footer/>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import Service from './service'
import { kulapApi } from '@/resources'
import PasswordMeter from 'vue-password-strength-meter'
import Header from '@/features/Header'
import Footer from '@/components/Footer'
import { showToastError } from '@/utils/helper'
import { RECAPTCHA_SITE_KEY } from '@/constants'

export default {
  name: 'ForgotPassword',
  components: {
    Header,
    Footer,
    PasswordMeter,
    VueRecaptcha
  },
  data () {
    return {
      service: null,
      newPassword: '',
      confirmNewPassword: '',
      passwordScore: null,
      isLoading: false,
      siteKey: RECAPTCHA_SITE_KEY
    }
  },
  computed: {
    passwordMeterLabel () {
      switch (this.passwordScore) {
        case 0: return 'Very weak'
        case 1: return 'Weak'
        case 2: return 'Normal'
        case 3: return 'Strong'
        case 4: return 'Very strong'
        default: return ''
      }
    }
  },
  async created () {
    this.service = new Service({
      store: this.$store,
      kulapApi
    })
    const query = this.$route.query
    if (!query.email || !query.token) {
      this.$buefy.toast.open({
        message: 'Invalid URL.',
        type: 'is-danger'
      })
      this.$router.push({ name: 'Exchange' })
    }
  },
  methods: {
    async executeRecaptcha () {
      try {
        const query = this.$route.query
        const validateFieldResult = await this.$validator.validate()
        if (query.email && query.token && validateFieldResult) {
          this.isLoading = true
          this.$refs.recaptcha.execute()
          this.$scrollTo('.reset-password--item')
        }
      } catch (e) {
        showToastError(e)
      }
    },
    onCaptchaExpired () {
      this.$refs.recaptcha.reset()
      this.isLoading = false
    },
    onPasswordScoreChange (score) {
      this.passwordScore = score
    },
    async reset (recaptchaToken) {
      this.isLoading = true
      try {
        const query = this.$route.query
        await this.service.resetPassword(query.email, this.newPassword, query.token, recaptchaToken)
        this.$buefy.toast.open({
          message: 'Reset password success.',
          type: 'is-success'
        })
        this.$router.push({ name: 'Exchange' })
      } catch (e) {
        showToastError(e)
      }
      this.$refs.recaptcha.reset()
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.reset-password {
  float: left;
  width: 100%;
  min-height: 100vh;

  &--item {
    float: left;
    width: 100%;
  }

  &--title {
    margin: 0 0 25px 0;
    font-size: 21px;
    font-weight: bold;
    text-align: center;
    color: #000000;
  }

  &--detail {
    color: grey;
    text-align: center;
  }
}
.section-gray {
  float: left;
  width: 100%;
  padding-bottom: 170px;
  text-align: center;
}
.section-white {
  display: inline-block;
  padding: 32px 47px;
  width: fit-content;
  max-width: 550px;
  border-radius: 5px;
  box-shadow: 0 2px 4px -3px rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
  text-align: left;
}
.status-name {
  font-size: 27px;
  font-weight: bold;
  vertical-align:middle
}
.submit-btn {
  float: left;
  width: 100%;
  text-align: center;
  color: #fff;
  background-color: #0458af;
  padding: 20px 10px;
  font-weight: bold;
  border-radius: 3px;
  border: unset;
  cursor: pointer;
}
.reset-password-field-box {
  text-align: left;
  margin-bottom: 15px;
}
.reset-password-password-field {
  flex-wrap: wrap;
}
.reset-password-field-note-text {
  font-size: 12px;
  letter-spacing: 0.3px;
  color: #939393;
  margin-left: 10px;
  line-height: 24px;

  &--hightlight {
    font-weight: bold;
    color: #ff4500;
  }
}
.password-input {
  width: 100%;
  border-bottom-left-radius: 4px;
  border-top-left-radius: 4px;
}
.password-meter {
  margin: 0;

  &--score {
    font-size: 13px;
    font-weight: bold;
  }
}
.very-weak {
  color: darkred
}
.weak {
  color: #ff4500
}
.normal {
  color: orange
}
.strong {
  color: #9acd32
}
.very-strong {
  color: green
}
</style>



// WEBPACK FOOTER //
// src/pages/ResetPassword/index.vue